import { useState, useEffect } from 'react';

type OS = 'windows' | 'macos' | 'linux';

export default function Download() {
  const [detectedOS, setDetectedOS] = useState<OS>('windows');
  const [linkCode] = useState('MOLD-XXXX-XXXX-XXXX');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // OS 자동 감지
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) {
      setDetectedOS('windows');
    } else if (userAgent.includes('mac')) {
      setDetectedOS('macos');
    } else if (userAgent.includes('linux')) {
      setDetectedOS('linux');
    }

    // TODO: API에서 앱 연동 코드 가져오기
    // fetchLinkCode().then(setLinkCode);
  }, []);

  const copyLinkCode = () => {
    navigator.clipboard.writeText(linkCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloads: Record<OS, { name: string; icon: string; file: string; size: string }> = {
    windows: {
      name: 'Windows',
      icon: '🪟',
      file: 'moldClaw_0.8.11_x64-setup.exe',
      size: '5 MB',
    },
    macos: {
      name: 'macOS',
      icon: '🍎',
      file: 'moldClaw_0.8.11_universal.dmg',
      size: '8 MB',
    },
    linux: {
      name: 'Linux',
      icon: '🐧',
      file: 'moldClaw_0.8.11_amd64.deb',
      size: '4 MB',
    },
  };

  return (
    <div className="py-12">
      <div className="section-container">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white dark:text-pure-white mb-4">
            moldClaw 다운로드
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300 dark:text-zinc-400">
            버전 0.8.11 • 2026년 3월 업데이트
          </p>
        </div>

        {/* Download Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          {(Object.entries(downloads) as [OS, typeof downloads[OS]][]).map(([os, info]) => (
            <div
              key={os}
              className={`card text-center cursor-pointer transition-all ${
                detectedOS === os
                  ? 'border-space-blue ring-2 ring-space-blue ring-opacity-20'
                  : 'hover:border-medium-gray'
              }`}
            >
              {detectedOS === os && (
                <span className="inline-block px-3 py-1 bg-space-blue text-white text-xs rounded-full mb-3">
                  추천
                </span>
              )}
              <div className="text-4xl mb-3">{info.icon}</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{info.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{info.size}</p>
              <a
                href={`/releases/${info.file}`}
                className="btn-primary w-full inline-block text-center"
                download
              >
                다운로드
              </a>
            </div>
          ))}
        </div>

        {/* App Link Code */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="card bg-space-blue-light border-space-blue">
            <div className="flex items-start gap-4">
              <div className="text-3xl">🔗</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">앱 연동 코드</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  moldClaw 앱을 설치한 후, 아래 코드를 입력하면 자동으로 로그인됩니다.
                </p>
                <div className="flex gap-2">
                  <code className="flex-1 px-4 py-2 bg-pure-white border border-border-gray rounded-lg font-mono text-gray-900 dark:text-white">
                    {linkCode}
                  </code>
                  <button
                    onClick={copyLinkCode}
                    className="px-4 py-2 bg-space-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {copied ? '복사됨!' : '복사'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  * 이 코드는 24시간 후 만료됩니다
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* System Requirements */}
        <div className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            시스템 요구사항
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                🪟 Windows
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <li>• Windows 10 이상 (64-bit)</li>
                <li>• RAM: 최소 4GB, 권장 8GB</li>
                <li>• 저장공간: 500MB 이상</li>
              </ul>
            </div>
            <div className="card">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                🍎 macOS
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <li>• macOS 11 (Big Sur) 이상</li>
                <li>• Intel 또는 Apple Silicon</li>
                <li>• RAM: 최소 4GB, 권장 8GB</li>
                <li>• 저장공간: 500MB 이상</li>
              </ul>
            </div>
            <div className="card">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                🐧 Linux
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <li>• Ubuntu 20.04+, Debian 11+</li>
                <li>• Fedora 35+, Arch Linux</li>
                <li>• RAM: 최소 4GB, 권장 8GB</li>
                <li>• 저장공간: 500MB 이상</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Installation Guide */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            설치 가이드
          </h2>
          
          <div className="space-y-6">
            {/* Windows */}
            <details className="card group">
              <summary className="flex items-center justify-between cursor-pointer">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🪟 Windows 설치 방법
                </h3>
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-4 text-gray-700 dark:text-gray-300 space-y-3">
                <p>1. 다운로드한 <code className="bg-gray-100 dark:bg-zinc-800 dark:bg-zinc-800 px-2 py-0.5 rounded">moldClaw_0.8.11_x64-setup.exe</code> 파일을 실행합니다.</p>
                <p>2. "Windows의 PC 보호" 경고가 나타나면 <strong>추가 정보</strong> → <strong>실행</strong>을 클릭합니다.</p>
                <p>3. 설치 마법사의 안내에 따라 설치를 완료합니다.</p>
                <p>4. 바탕화면의 moldClaw 아이콘을 더블클릭하여 실행합니다.</p>
              </div>
            </details>

            {/* macOS */}
            <details className="card group">
              <summary className="flex items-center justify-between cursor-pointer">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🍎 macOS 설치 방법
                </h3>
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-4 text-gray-700 dark:text-gray-300 space-y-3">
                <p>1. 다운로드한 <code className="bg-gray-100 dark:bg-zinc-800 dark:bg-zinc-800 px-2 py-0.5 rounded">moldClaw_0.8.11_universal.dmg</code> 파일을 엽니다.</p>
                <p>2. moldClaw 앱을 Applications 폴더로 드래그합니다.</p>
                <p>3. 처음 실행 시 "확인되지 않은 개발자" 경고가 나타나면:</p>
                <ul className="pl-4 space-y-1">
                  <li>• <strong>시스템 설정</strong> → <strong>개인정보 보호 및 보안</strong>으로 이동</li>
                  <li>• "moldClaw" 옆의 <strong>확인 없이 열기</strong> 클릭</li>
                </ul>
                <p>4. Launchpad에서 moldClaw를 실행합니다.</p>
              </div>
            </details>

            {/* Linux */}
            <details className="card group">
              <summary className="flex items-center justify-between cursor-pointer">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  🐧 Linux 설치 방법
                </h3>
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-4 text-gray-700 dark:text-gray-300 space-y-3">
                <p><strong>Ubuntu/Debian (.deb):</strong></p>
                <code className="block bg-gray-100 dark:bg-zinc-800 dark:bg-zinc-800 p-3 rounded text-sm">
                  sudo dpkg -i moldClaw_0.8.11_amd64.deb
                </code>
                
                <p className="pt-2"><strong>Fedora/RHEL (.rpm):</strong></p>
                <code className="block bg-gray-100 dark:bg-zinc-800 dark:bg-zinc-800 p-3 rounded text-sm">
                  sudo rpm -i moldClaw_0.8.11_x86_64.rpm
                </code>
                
                <p className="pt-2"><strong>AppImage (모든 배포판):</strong></p>
                <code className="block bg-gray-100 dark:bg-zinc-800 dark:bg-zinc-800 p-3 rounded text-sm">
                  chmod +x moldClaw_0.8.11_x86_64.AppImage<br />
                  ./moldClaw_0.8.11_x86_64.AppImage
                </code>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
}
